import React, { FC, memo, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { formatFullName } from "@/data/persons";
import { IUser } from "@/data/users";
import { Flex, FloatedList } from "@/eworkitComponents";
import { IFilterComponentProps, useOnChangeFilterHandler } from "@/infrastructure/Filter";

import { IUsersSelectProps, UsersSelect } from "../Codelists";
import { SkillRemove } from "../Forms/Skills/components";

type UserLike = Partial<IUser>;

interface Props extends IFilterComponentProps<UserLike[]>, Omit<IUsersSelectProps, "name" | "onChange" | "value"> {}

const simplifyUser = ({ id, firstName, lastName }: UserLike): UserLike => ({ id, firstName, lastName });

export const AssigneeFilter: FC<Props> = memo(({ name, value = [], onChange, ...rest }) => {
  const handleFilterChange = useOnChangeFilterHandler(name, onChange);
  const { t } = useTranslation("session");

  const handleChange = useCallback(
    (newValue: UserLike[]) => {
      handleFilterChange([...value, ...newValue.map(simplifyUser)]);
    },
    [handleFilterChange, value],
  );

  const filterAfterFetch = useCallback(
    (options: UserLike[]) => options.filter(o => !value.some(v => v.id === o.id)),
    [value],
  );

  const handleRemoveClick = useCallback(
    (user: UserLike) => {
      handleFilterChange(value.filter(v => v.id !== user?.id));
    },
    [value, handleFilterChange],
  );

  return (
    <Flex direction="column" innerMargin="sm">
      <UsersSelect
        name={name}
        placeholder={t("TASKS.FILTERS.ASSIGNEE_PLACEHOLDER")}
        onChange={handleChange}
        filterAfterFetch={filterAfterFetch}
        isMulti
        value={[]}
        {...rest}
      />

      {value && (
        <FloatedList>
          {value.map(user => (
            <Flex key={user.id} align="center">
              <SkillRemove
                size="md"
                color="secondary"
                text={formatFullName(user)}
                onClick={() => handleRemoveClick(user)}
              />
            </Flex>
          ))}
        </FloatedList>
      )}
    </Flex>
  );
});
